// =====================================================
// logger.js — TEF CMS Frontend Error Logger (Phase 12)
// Globale Fehlererfassung + Backend-Reporting
// =====================================================

/** Rate-Limiting: Max. 10 Fehler pro Minute */
let _errorCount = 0;
let _errorResetTimer = null;
const MAX_ERRORS_PER_MINUTE = 10;

/** Duplikat-Guard: Gleiche Fehler innerhalb von 5s blockieren */
const _recentErrors = new Map();
const DEDUP_WINDOW_MS = 5000;

/**
 * Sende einen Fehler an das Backend POST /api/errors
 * @param {Object} errorData
 * @param {string} errorData.type - js_error | promise_rejection | api_error | load_error | route_error | validation_error
 * @param {string} errorData.message - Technische Fehlermeldung
 * @param {string} [errorData.source] - Betroffene Datei/Modul
 * @param {string} [errorData.page] - Betroffene URL
 * @param {string} [errorData.stack_trace] - Stack Trace
 * @param {Object} [errorData.metadata] - Zusätzliche Infos
 */
export function reportError(errorData) {
  if (typeof window === 'undefined') return;
  
  // Rate-Limiting
  _errorCount++;
  if (_errorCount > MAX_ERRORS_PER_MINUTE) {
    return; // Stoppe, verhindere Endlos-Loops
  }
  if (!_errorResetTimer) {
    _errorResetTimer = setTimeout(() => {
      _errorCount = 0;
      _errorResetTimer = null;
    }, 60000);
  }
  
  // Duplikat-Guard: Gleiche message innerhalb 5s unterdrücken
  const dedupKey = (errorData.message || '').substring(0, 100);
  if (_recentErrors.has(dedupKey)) return;
  _recentErrors.set(dedupKey, true);
  setTimeout(() => _recentErrors.delete(dedupKey), DEDUP_WINDOW_MS);
  
  // Metadata anreichern
  const enriched = {
    ...errorData,
    page: errorData.page || window.location.href,
    metadata: {
      ...(errorData.metadata || {}),
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      viewport: `${window.innerWidth}x${window.innerHeight}`,
    }
  };
  
  // User-Role aus localStorage auslesen (falls vorhanden)
  try {
    const token = localStorage.getItem('tef_token');
    if (token) {
      const payload = JSON.parse(atob(token.split('.')[1]));
      enriched.user_role = payload.role || 'unknown';
    }
  } catch (e) { /* ignore */ }
  
  // An Backend senden (fire-and-forget, kein await)
  try {
    fetch('/api/errors', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(enriched),
    }).catch(() => { /* Netzwerkfehler beim Error-Reporting ignorieren */ });
  } catch (e) { /* ignore */ }
}

/**
 * Logge einen Fehler in die Konsole UND sende ihn ans Backend
 */
export function logError(message, error, context = {}) {
  console.error(`[TEF] ${message}`, { error, ...context });
  
  reportError({
    type: context.type || 'js_error',
    message: typeof error === 'string' ? error : (error?.message || message),
    source: context.source || context.filename || 'frontend',
    stack_trace: error?.stack || '',
    metadata: {
      originalMessage: message,
      ...context,
    },
  });
}

/**
 * Wrapper für fetch() mit automatischem API-Fehler-Logging
 * @param {string} url
 * @param {RequestInit} options
 * @returns {Promise<Response>}
 */
export async function trackedFetch(url, options = {}) {
  try {
    const response = await fetch(url, options);
    if (!response.ok) {
      reportError({
        type: 'api_error',
        message: `API ${options.method || 'GET'} ${url} failed with status ${response.status}`,
        source: url,
        metadata: { status: response.status, method: options.method || 'GET' },
      });
    }
    return response;
  } catch (err) {
    reportError({
      type: 'api_error',
      message: `API ${options.method || 'GET'} ${url} failed: ${err.message}`,
      source: url,
      stack_trace: err.stack,
      metadata: { method: options.method || 'GET' },
    });
    throw err;
  }
}

/**
 * Installiere globale Fehlerhandler (window.error + unhandledrejection)
 * Erweitert um Backend-Reporting
 */
export function installGlobalErrorHandlers() {
  if (typeof window === 'undefined') {
    return;
  }

  if (window.__tefGlobalErrorHandlersInstalled) {
    return;
  }

  window.__tefGlobalErrorHandlersInstalled = true;

  // JavaScript-Fehler
  window.addEventListener('error', (event) => {
    // Unterscheide: Skript-Fehler vs. Ressource-Ladefehler
    if (event.target && event.target !== window && (event.target.tagName === 'IMG' || event.target.tagName === 'SCRIPT' || event.target.tagName === 'LINK')) {
      // Ressource konnte nicht geladen werden
      reportError({
        type: 'load_error',
        message: `Ressource konnte nicht geladen werden: ${event.target.src || event.target.href || 'unbekannt'}`,
        source: event.target.tagName.toLowerCase(),
        metadata: { tag: event.target.tagName, src: event.target.src || event.target.href },
      });
    } else {
      // JavaScript runtime error
      logError('Unhandled window error', event.error || event.message, {
        type: 'js_error',
        filename: event.filename,
        line: event.lineno,
        column: event.colno
      });
    }
  }, true); // Capture Phase für Ressource-Fehler

  // Promise Rejections
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    reportError({
      type: 'promise_rejection',
      message: typeof reason === 'string' ? reason : (reason?.message || 'Unhandled Promise Rejection'),
      stack_trace: reason?.stack || '',
      source: 'promise',
    });
    logError('Unhandled promise rejection', reason);
  });
}
